import type { DomainMessagesTypes } from 'shared/domain/messages/message';
import { defineChannelContract, ChannelContract, ChannelContractShape } from '../contract';

/**
 * Contract of an entity the service worker keeps in IndexedDB: the whole
 * collection on request, a sync from the API, the events of `TEvents` and the
 * module's own requests (`TRequests`, e.g. mutations).
 */
export interface EntityContractShape<
  TModel,
  TEvents extends ChannelContractShape['events'],
  TRequests extends ChannelContractShape['requests'] = Record<never, never>,
> extends ChannelContractShape {
  requests: TRequests & {
    getAll: { request: undefined; response: TModel[] };
    sync: { request: undefined; response: void };
  };
  events: TEvents;
}

/** One item by id, archived ones included; add it to `TRequests` of entities with details views. */
export type EntityByIdRequests<TModel> = {
  getOneById: { request: { id: string }; response: TModel };
};

export interface EntityContract<
  TModel,
  TEvents extends ChannelContractShape['events'],
  TRequests extends ChannelContractShape['requests'] = Record<never, never>,
> extends ChannelContract<EntityContractShape<TModel, TEvents, TRequests>> {
  /** Broadcast with the whole collection (`{ items, hasAll }`) after each sync. */
  readonly collectionEvent: DomainMessagesTypes;
}

export interface EntityContractOptions {
  collectionEvent: DomainMessagesTypes;
}

export function defineEntityContract<
  TModel,
  TEvents extends ChannelContractShape['events'],
  TRequests extends ChannelContractShape['requests'] = Record<never, never>,
>(key: string, { collectionEvent }: EntityContractOptions): EntityContract<TModel, TEvents, TRequests> {
  return { ...defineChannelContract<EntityContractShape<TModel, TEvents, TRequests>>(key), collectionEvent };
}
